// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/formula-evaluator.ts
// Bộ tính công thức lương tự viết: không dùng eval, chỉ cho phép các hàm trong danh sách dưới.

const ALLOWED_FUNCTIONS = new Set([
    'IF', 'IFS', 'SWITCH', 'VLOOKUP', 'LOOKUP',
    'ROUND', 'ROUNDUP', 'ROUNDDOWN', 'CEILING', 'FLOOR',
    'SUM', 'MIN', 'MAX', 'AVERAGE', 'AND', 'OR', 'ABS',
]);

export class PayrollFormulaError extends Error {
    constructor(message) {
        super(message);
        this.name = 'PayrollFormulaError';
    }
}

function normalizeAliasMap(aliases = {}) {
    return Object.fromEntries(Object.entries(aliases).map(([alias, code]) => [alias.toUpperCase(), code.toUpperCase()]));
}

function canonicalIdentifier(name, aliases) {
    const upper = name.toUpperCase();
    return ALLOWED_FUNCTIONS.has(upper) || upper === 'TRUE' || upper === 'FALSE' ? upper : aliases[upper] ?? upper;
}

function tokenize(source, aliases = {}) {
    const normalizedAliases = normalizeAliasMap(aliases);
    const text = source.trim().replace(/^=/, '');
    const tokens = [];
    let index = 0;
    while (index < text.length) {
        const char = text[index];
        if (/\s/.test(char)) { index += 1; continue; }
        const number = text.slice(index).match(/^\d+(?:[.,]\d+)?/);
        if (number) {
            tokens.push({ type: 'number', value: number[0].replace(',', '.') });
            index += number[0].length;
            continue;
        }
        const identifier = text.slice(index).match(/^[A-Za-z_][A-Za-z0-9_]*/);
        if (identifier) {
            tokens.push({ type: 'identifier', value: canonicalIdentifier(identifier[0], normalizedAliases) });
            index += identifier[0].length;
            continue;
        }
        const two = text.slice(index, index + 2);
        if (['>=', '<=', '==', '!=', '<>', '&&', '||'].includes(two)) {
            tokens.push({ type: 'operator', value: two });
            index += 2;
            continue;
        }
        if ('+-*/><='.includes(char)) tokens.push({ type: 'operator', value: char });
        else if ('()'.includes(char)) tokens.push({ type: 'paren', value: char });
        else if (char === ';' || char === ',') tokens.push({ type: 'comma', value: ',' });
        else throw new PayrollFormulaError(`Ký tự không hợp lệ trong công thức: ${char}`);
        index += 1;
    }
    tokens.push({ type: 'eof', value: '' });
    return tokens;
}

function truthy(value) { return value !== 0; }

function callFunction(name, args) {
    switch (name) {
        case 'IF':
            if (args.length < 2 || args.length > 3) throw new PayrollFormulaError('IF cần 2 hoặc 3 tham số.');
            return truthy(args[0]) ? args[1] : (args[2] ?? 0);
        case 'IFS':
            if (args.length < 2 || args.length % 2 !== 0) throw new PayrollFormulaError('IFS cần các cặp điều kiện và kết quả.');
            for (let i = 0; i < args.length; i += 2) if (truthy(args[i])) return args[i + 1];
            return 0;
        // SWITCH và VLOOKUP: các cặp khóa/kết quả, có thể kèm một tham số cuối làm mặc định. Không khớp
        // và không có mặc định thì trả 0 thay vì ném lỗi, để cả bảng lương không vỡ vì một dòng.
        case 'SWITCH': {
            if (args.length < 3) throw new PayrollFormulaError('SWITCH cần biểu thức và ít nhất một cặp khoá, kết quả.');
            const expr = args[0];
            const hasDefault = (args.length - 1) % 2 === 1;
            const pairsEnd = hasDefault ? args.length - 1 : args.length;
            for (let i = 1; i < pairsEnd; i += 2) if (expr === args[i]) return args[i + 1];
            return hasDefault ? args[args.length - 1] : 0;
        }
        case 'VLOOKUP': {
            if (args.length < 3) throw new PayrollFormulaError('VLOOKUP cần khoá tra cứu và ít nhất một cặp khoá, giá trị.');
            const [key, ...rest] = args;
            const hasDefault = rest.length % 2 === 1;
            const pairsEnd = hasDefault ? rest.length - 1 : rest.length;
            for (let i = 0; i < pairsEnd; i += 2) if (rest[i] === key) return rest[i + 1];
            return hasDefault ? rest[rest.length - 1] : 0;
        }
        // LOOKUP khớp theo khoảng: chọn mốc lớn nhất không vượt quá giá trị, mốc phải viết tăng dần.
        case 'LOOKUP': {
            if (args.length < 3 || args.length % 2 !== 1) throw new PayrollFormulaError('LOOKUP cần giá trị tra cứu và các cặp mốc, kết quả.');
            const value = args[0];
            let matched = 0;
            for (let i = 1; i < args.length; i += 2) if (value >= args[i]) matched = args[i + 1];
            return matched;
        }
        case 'ROUND': {
            if (args.length < 1 || args.length > 2) throw new PayrollFormulaError('ROUND cần 1 hoặc 2 tham số.');
            const digits = Math.trunc(args[1] ?? 0);
            const factor = 10 ** digits;
            return Math.round((args[0] + Number.EPSILON) * factor) / factor;
        }
        case 'ROUNDUP': {
            if (args.length < 1 || args.length > 2) throw new PayrollFormulaError('ROUNDUP cần 1 hoặc 2 tham số.');
            const digits = Math.trunc(args[1] ?? 0);
            const factor = 10 ** digits;
            return Math.ceil(args[0] * factor) / factor;
        }
        case 'ROUNDDOWN': {
            if (args.length < 1 || args.length > 2) throw new PayrollFormulaError('ROUNDDOWN cần 1 hoặc 2 tham số.');
            const digits = Math.trunc(args[1] ?? 0);
            const factor = 10 ** digits;
            return Math.floor(args[0] * factor) / factor;
        }
        case 'CEILING': {
            if (args.length < 1 || args.length > 2) throw new PayrollFormulaError('CEILING cần 1 hoặc 2 tham số.');
            const step = args[1] ?? 1;
            if (step === 0) throw new PayrollFormulaError('Bước làm tròn của CEILING không được bằng 0.');
            return Math.ceil(args[0] / step) * step;
        }
        case 'FLOOR': {
            if (args.length < 1 || args.length > 2) throw new PayrollFormulaError('FLOOR cần 1 hoặc 2 tham số.');
            const step = args[1] ?? 1;
            if (step === 0) throw new PayrollFormulaError('Bước làm tròn của FLOOR không được bằng 0.');
            return Math.floor(args[0] / step) * step;
        }
        case 'SUM': return args.reduce((sum, value) => sum + value, 0);
        case 'MIN': return args.length ? Math.min(...args) : 0;
        case 'MAX': return args.length ? Math.max(...args) : 0;
        case 'AVERAGE': return args.length ? args.reduce((sum, value) => sum + value, 0) / args.length : 0;
        case 'AND': return args.every(truthy) ? 1 : 0;
        case 'OR': return args.some(truthy) ? 1 : 0;
        case 'ABS':
            if (args.length !== 1) throw new PayrollFormulaError('ABS cần đúng 1 tham số.');
            return Math.abs(args[0]);
        default: throw new PayrollFormulaError(`Hàm không được phép: ${name}`);
    }
}

class Parser {
    constructor(tokens, variables) {
        this.tokens = tokens;
        this.variables = variables;
        this.index = 0;
    }

    parse() {
        const value = this.parseOr();
        if (this.current().type !== 'eof') throw new PayrollFormulaError(`Cú pháp không hợp lệ gần: ${this.current().value}`);
        return value;
    }

    current() { return this.tokens[this.index]; }
    consume() { return this.tokens[this.index++]; }
    match(type, value) {
        const token = this.current();
        if (token.type !== type || (value !== undefined && token.value !== value)) return false;
        this.index += 1;
        return true;
    }

    parseOr() {
        let left = this.parseAnd();
        while (this.match('operator', '||')) left = truthy(left) || truthy(this.parseAnd()) ? 1 : 0;
        return left;
    }
    parseAnd() {
        let left = this.parseComparison();
        while (this.match('operator', '&&')) left = truthy(left) && truthy(this.parseComparison()) ? 1 : 0;
        return left;
    }
    parseComparison() {
        let left = this.parseAdditive();
        while (this.current().type === 'operator' && ['>', '<', '>=', '<=', '=', '==', '!=', '<>'].includes(this.current().value)) {
            const operator = this.consume().value;
            const right = this.parseAdditive();
            if (operator === '>') left = left > right ? 1 : 0;
            else if (operator === '<') left = left < right ? 1 : 0;
            else if (operator === '>=') left = left >= right ? 1 : 0;
            else if (operator === '<=') left = left <= right ? 1 : 0;
            else if (operator === '=' || operator === '==') left = left === right ? 1 : 0;
            else left = left !== right ? 1 : 0;
        }
        return left;
    }
    parseAdditive() {
        let left = this.parseMultiplicative();
        while (this.current().type === 'operator' && ['+', '-'].includes(this.current().value)) {
            const operator = this.consume().value;
            const right = this.parseMultiplicative();
            left = operator === '+' ? left + right : left - right;
        }
        return left;
    }
    parseMultiplicative() {
        let left = this.parseUnary();
        while (this.current().type === 'operator' && ['*', '/'].includes(this.current().value)) {
            const operator = this.consume().value;
            const right = this.parseUnary();
            if (operator === '/' && right === 0) throw new PayrollFormulaError('Không thể chia cho 0.');
            left = operator === '*' ? left * right : left / right;
        }
        return left;
    }
    parseUnary() {
        if (this.match('operator', '-')) return -this.parseUnary();
        if (this.match('operator', '+')) return this.parseUnary();
        return this.parsePrimary();
    }
    parsePrimary() {
        const token = this.current();
        if (this.match('number')) return Number(token.value);
        if (this.match('paren', '(')) {
            const value = this.parseOr();
            if (!this.match('paren', ')')) throw new PayrollFormulaError('Thiếu dấu đóng ngoặc.');
            return value;
        }
        if (token.type === 'identifier') {
            const name = this.consume().value;
            if (this.match('paren', '(')) {
                if (!ALLOWED_FUNCTIONS.has(name)) throw new PayrollFormulaError(`Hàm không được phép: ${name}`);
                const args = [];
                // Mọi tham số được tính trước khi gọi hàm, kể cả nhánh không được chọn: IF(NCC > 0, MLT / NCC, 0)
                // vẫn báo chia cho 0 khi NCC = 0. Phép chia rủi ro nên tách sang biến trung gian.
                if (!this.match('paren', ')')) {
                    do { args.push(this.parseOr()); } while (this.match('comma'));
                    if (!this.match('paren', ')')) throw new PayrollFormulaError(`Thiếu dấu đóng ngoặc của ${name}.`);
                }
                return callFunction(name, args);
            }
            if (name === 'TRUE') return 1;
            if (name === 'FALSE') return 0;
            if (!(name in this.variables)) throw new PayrollFormulaError(`Biến không tồn tại: ${name}`);
            return this.variables[name];
        }
        throw new PayrollFormulaError(`Cú pháp không hợp lệ gần: ${token.value || 'kết thúc'}`);
    }
}

export function evaluatePayrollFormula(formula, variables, aliases = {}) {
    const normalizedAliases = normalizeAliasMap(aliases);
    const normalized = Object.fromEntries(Object.entries(variables).map(([key, value]) => [canonicalIdentifier(key, normalizedAliases), Number(value) || 0]));
    const result = new Parser(tokenize(formula, normalizedAliases), normalized).parse();
    if (!Number.isFinite(result)) throw new PayrollFormulaError('Kết quả công thức không hợp lệ.');
    return result;
}

export function extractPayrollDependencies(formula, aliases = {}) {
    return Array.from(new Set(tokenize(formula, aliases)
        .filter((token) => token.type === 'identifier' && !ALLOWED_FUNCTIONS.has(token.value) && token.value !== 'TRUE' && token.value !== 'FALSE')
        .map((token) => token.value)));
}

export function sortPayrollFormulas(formulas, availableVariables = [], aliases = {}) {
    const normalizedAliases = normalizeAliasMap(aliases);
    const formulaCodes = new Set(Object.keys(formulas).map((code) => canonicalIdentifier(code, normalizedAliases)));
    const available = new Set(Array.from(availableVariables, (code) => canonicalIdentifier(code, normalizedAliases)));
    const dependencies = new Map();
    for (const [rawCode, formula] of Object.entries(formulas)) {
        const code = canonicalIdentifier(rawCode, normalizedAliases);
        const refs = extractPayrollDependencies(formula, normalizedAliases);
        const missing = refs.filter((ref) => !formulaCodes.has(ref) && !available.has(ref));
        if (missing.length) throw new PayrollFormulaError(`Biến không tồn tại trong ${code}: ${missing.join(', ')}`);
        dependencies.set(code, refs.filter((ref) => formulaCodes.has(ref)));
    }

    const order = [];
    const state = new Map();
    const visit = (code, path) => {
        if (state.get(code) === 'done') return;
        if (state.get(code) === 'visiting') throw new PayrollFormulaError(`Phát hiện vòng lặp công thức: ${[...path, code].join(' -> ')}`);
        state.set(code, 'visiting');
        for (const dependency of dependencies.get(code) ?? []) visit(dependency, [...path, code]);
        state.set(code, 'done');
        order.push(code);
    };
    for (const code of formulaCodes) visit(code, []);
    return order;
}

export function evaluatePayrollFormulaSet(formulas, baseValues, aliases = {}) {
    const normalizedAliases = normalizeAliasMap(aliases);
    const values = Object.fromEntries(Object.entries(baseValues).map(([key, value]) => [canonicalIdentifier(key, normalizedAliases), Number(value) || 0]));
    const normalizedFormulas = Object.fromEntries(Object.entries(formulas).map(([key, value]) => [canonicalIdentifier(key, normalizedAliases), value]));
    const order = sortPayrollFormulas(normalizedFormulas, Object.keys(values), normalizedAliases);
    for (const code of order) {
        try {
            values[code] = evaluatePayrollFormula(normalizedFormulas[code], values, normalizedAliases);
        } catch (error) {
            // Nêu rõ biến và công thức hỏng, nếu không "Không thể chia cho 0" giữa hàng chục công thức rất khó lần.
            const detail = error instanceof Error ? error.message : String(error);
            throw new PayrollFormulaError(`Biến ${code} (${normalizedFormulas[code]}): ${detail}`);
        }
    }
    return { values, order };
}

export function normalizePayrollFormulaAliases(formula, aliases) {
    return tokenize(formula, aliases)
        .filter((token) => token.type !== 'eof')
        .map((token) => token.value)
        .join(' ')
        .replace(/\(\s+/g, '(')
        .replace(/\s+\)/g, ')')
        .replace(/\s+,\s+/g, ', ');
}
